import { QueryClient } from '@tanstack/react-query';
import apiClient from '../utils/api';

// Create a query client with optimized defaults for production
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 5 minutes by default
      staleTime: 5 * 60 * 1000,
      // Keep cache for 10 minutes
      cacheTime: 10 * 60 * 1000,
      // Retry failed requests 3 times with exponential backoff
      retry: (failureCount, error) => {
        if (error.response?.status === 404) return false;
        if (error.response?.status === 401) return false;
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Refetch on window focus in production
      refetchOnWindowFocus: process.env.NODE_ENV === 'production',
      // Don't refetch on reconnect (too aggressive)
      refetchOnReconnect: false,
    },
    mutations: {
      // Retry mutations once
      retry: 1,
      retryDelay: 1000,
    },
  },
});

// Query keys factory for consistent cache keys
export const queryKeys = {
  // Auth
  auth: {
    user: ['auth', 'user'],
  },
  
  // Decks
  decks: {
    all: ['decks'],
    list: (filters) => ['decks', 'list', filters],
    detail: (id) => ['decks', 'detail', id],
    categories: ['decks', 'categories'],
  },
  
  // Teams
  teams: {
    all: ['teams'],
    list: (filters) => ['teams', 'list', filters],
    detail: (id) => ['teams', 'detail', id],
  },
  
  // TCG Matches
  tcgMatches: {
    all: ['tcgMatches'],
    list: (filters) => ['tcgMatches', 'list', filters],
    detail: (id) => ['tcgMatches', 'detail', id],
    stats: (deckId) => ['tcgMatches', 'stats', deckId],
  },
  
  // VGC Matches
  vgcMatches: {
    all: ['vgcMatches'],
    list: (filters) => ['vgcMatches', 'list', filters],
    detail: (id) => ['vgcMatches', 'detail', id],
    stats: (teamId) => ['vgcMatches', 'stats', teamId],
  },
  
  // Analytics
  analytics: {
    overall: ['analytics', 'overall'],
    deck: (deckId) => ['analytics', 'deck', deckId],
    team: (teamId) => ['analytics', 'team', teamId],
    trends: (timeframe) => ['analytics', 'trends', timeframe],
  },
  
  // Friends
  friends: {
    all: ['friends'],
    list: ['friends', 'list'],
    requests: ['friends', 'requests'],
  },
  
  // Notifications
  notifications: {
    all: ['notifications'],
    unread: ['notifications', 'unread'],
  },
};

// Prefetch helpers
export const prefetchHelpers = {
  // Prefetch user data on app load
  prefetchUserData: async () => {
    await queryClient.prefetchQuery({
      queryKey: queryKeys.auth.user,
      queryFn: () => apiClient.get('/api/auth/user'),
      staleTime: Infinity, // User data rarely changes
    });
  },
  
  // Prefetch deck list when navigating
  prefetchDecks: async () => {
    await queryClient.prefetchQuery({
      queryKey: queryKeys.decks.all,
      queryFn: () => apiClient.get('/api/decks'),
    });
  },
  
  // Prefetch match data
  prefetchMatches: async (gameMode) => {
    const endpoint = gameMode === 'TCG' ? '/api/tcgmatches' : '/api/vgcmatches';
    const queryKey = gameMode === 'TCG' ? queryKeys.tcgMatches.all : queryKeys.vgcMatches.all;
    
    await queryClient.prefetchQuery({
      queryKey,
      queryFn: () => apiClient.get(endpoint),
    });
  },
};

// Cache invalidation helpers
export const cacheHelpers = {
  // Invalidate all deck-related queries
  invalidateDecks: () => {
    queryClient.invalidateQueries({ queryKey: ['decks'] });
  },
  
  // Invalidate all match-related queries
  invalidateMatches: (gameMode) => {
    const key = gameMode === 'TCG' ? 'tcgMatches' : 'vgcMatches';
    queryClient.invalidateQueries({ queryKey: [key] });
  },
  
  // Clear all cache (useful for logout)
  clearAllCache: () => {
    queryClient.clear();
  },
};